import { FC, useEffect, useState } from "react";

import {
  Column,
  Row,
  Combobox,
  FormField,
  TextInput,
  useToast,
  Button,
} from "@hightouch/htui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { captureException } from "src/lib/monitoring";
import { usePartnerSyncOutletContext } from "./outlet";
import pick from "lodash/pick";

import { Explore } from "src/components/explore";
import {
  ModelColumn,
  useUpdateModelMutation,
  useSaveModelConfigurationMutation,
} from "src/graphql";
import { DeleteColumnWarningModal } from "src/components/models/reload-model/delete-column-warning-modal";
import { BottomBar } from "src/partner/bottom-bar";
import {
  getSaveModelConfigurationInputFromState,
  UpdateQueryColumnType,
  useModelRun,
  useUpdateQuery,
} from "src/utils/models";
import { useModelState } from "src/hooks/use-model-state";

type RawColumn = { name: string; type: string; raw_type: string | null };

const mapColumnToColumnInput = (
  column: ModelColumn | RawColumn,
): UpdateQueryColumnType => {
  const filteredColumn = pick(column, [
    "metadata",
    "name",
    "raw_type",
    "top_k_enabled",
    "type",
  ]);

  return {
    ...filteredColumn,
    name: filteredColumn.name ?? "",
    type: filteredColumn.type ?? "",
  };
};

export const Model: FC = () => {
  const { sync } = usePartnerSyncOutletContext();
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState<string>(sync.segment?.name ?? "");
  const [blockedColumns, setBlockedColumns] = useState<string[] | null>(null);
  const { enableModelColumnResolutionViaBackend } = useFlags();

  const updateMutation = useUpdateModelMutation();
  const { mutateAsync: saveModelConfiguration } =
    useSaveModelConfigurationMutation();
  const updateQuery = useUpdateQuery();

  const model = sync.segment!;
  const source = model?.connection;

  const supportsResultSchema = source?.definition?.supportsResultSchema;

  const modelState = useModelState(model);

  const {
    runQuery,
    cancelQuery,
    resetRunState,
    loading: queryLoading,
    error: queryError,
    errorAtLine: queryErrorAtLine,
    rows,
    numRowsWithoutLimit,
    isResultTruncated,
    columns,
    asyncPagination,
    rowsCount,
    rawColumns,
    page,
    getSchema,
    setPage,
    roleResolution,
  } = useModelRun(modelState.state);

  const save = async () => {
    setIsSaving(true);

    try {
      await updateMutation.mutateAsync({
        id: model?.id,
        input: {
          name,
          primary_key: modelState.state.primary_key,
          query_type: modelState.state.query_type,
        },
      });

      if (!enableModelColumnResolutionViaBackend) {
        let newColumns = rawColumns?.length ? rawColumns : model.columns;
        if (supportsResultSchema && !modelState.isPreviewFresh) {
          const { data, error } = await getSchema();
          if (error) {
            toast({
              id: "query-schema-error",
              title: "Error previewing query schema",
              variant: "error",
            });
            captureException(error);
          } else if (data?.columns.length) {
            newColumns = data.columns;
          }
        }

        await updateQuery({
          model: modelState.state,
          columns: newColumns.map(mapColumnToColumnInput),
          overwriteMetadata: true,
        });
      } else {
        const result = await saveModelConfiguration({
          modelId: String(model.id),
          input: getSaveModelConfigurationInputFromState(modelState.state),
        });
        const errors = result.saveModelConfiguration.errors;
        if (errors?.length) {
          setBlockedColumns(errors.map((error) => error.column_name));
          return;
        }
      }

      toast({
        id: "update-model-query",
        title: "Model was updated",
        variant: "success",
      });
    } catch (error) {
      captureException(error);
      toast({
        id: "update-model-query",
        title: "Couldn't update model",
        variant: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    modelState.reset(model);
    resetRunState();
  }, [model]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (columns?.length) {
      modelState.setIsPreviewFresh(true);
    }
  }, [rows, columns]);

  if (!model) {
    return null;
  }

  return (
    <>
      <Column p={4} flex={1}>
        <Row gap={4} mb={6} maxWidth="300px">
          <FormField label="Model name">
            <TextInput value={name} onChange={(e) => setName(e.target.value)} />
          </FormField>
          <FormField label="Primary key">
            <Combobox
              options={model.columns}
              onChange={(primary_key) => {
                modelState.onChange({ primary_key });
              }}
              value={modelState.state.primary_key}
              optionLabel={(column) => column.alias ?? column.name}
              optionDescription={(column) => column.description ?? ""}
              optionValue={(column) => column.name}
            />
          </FormField>
        </Row>
        <Explore
          limit
          isEmbeddedFlow
          modelState={modelState}
          isPlaceholder={false}
          asyncPagination={asyncPagination}
          cancelQuery={cancelQuery}
          columns={columns}
          error={queryError}
          errorAtLine={queryErrorAtLine}
          isResultTruncated={Boolean(isResultTruncated)}
          loading={queryLoading}
          numRowsWithoutLimit={numRowsWithoutLimit}
          page={page}
          rows={rows}
          rowsCount={rowsCount}
          runQuery={runQuery}
          source={source}
          onPageChange={setPage}
          rowsPerPage={100}
          roleResolution={roleResolution}
        />
      </Column>

      <BottomBar>
        <Button variant="primary" size="lg" onClick={save} isLoading={isSaving}>
          Save changes
        </Button>
      </BottomBar>

      {blockedColumns && (
        <DeleteColumnWarningModal
          modelId={String(model.id)}
          modelName={model.name}
          columnNames={blockedColumns}
          onReset={() => setBlockedColumns(null)}
        />
      )}
    </>
  );
};
